// ============================================================================
// Settings — platform preferences (MVP)
// ----------------------------------------------------------------------------
// Reached from the top-right user dropdown (route: /settings). Two focused
// areas that map to real platform behaviour:
//   • General   — interface language (applies via i18n).
//   • Federated Training defaults — the starting hyperparameters used by the
//     run Configuration screen. Saved to localStorage via the shared
//     trainingDefaults module, so a new run config opens pre-filled with these.
// Styling mirrors the Users & Roles page (segmented tab bar, white cards).
// ============================================================================
import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  useTheme,
  TextField,
  MenuItem,
  Button,
  Snackbar,
  Alert,
  InputAdornment,
} from "@mui/material";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/AuthContext";
import { useAppContext } from "../../contexts/AppContext";
import {
  DEFAULT_TRAINING_DEFAULTS,
  loadTrainingDefaults,
  loadUiPrefs,
  fetchTrainingDefaults,
  saveTrainingDefaults,
  saveUiPrefs,
} from "../../config/trainingDefaults";

import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import RestartAltOutlinedIcon from "@mui/icons-material/RestartAltOutlined";
import SecurityMfaCard from "../account/components/SecurityMfaCard";
import { apiPost } from "../../utils/api";

const MONO_FONT =
  '"JetBrains Mono", "SF Mono", "Roboto Mono", ui-monospace, monospace';

// Tabular model architectures — mirrors MODEL_OPTIONS_BY_PIPELINE.tabular in
// scenes/configs/index.jsx (the default pipeline is tabular).
const MODEL_OPTIONS = [
  { value: "logreg", label: "Logistic Regression" },
  { value: "mlp", label: "Multi-Layer Perceptron" },
  { value: "tabnet", label: "TabNet" },
  { value: "fttransformer", label: "FT-Transformer" },
];

const TABS = [
  { key: "general", label: "General", icon: <TuneOutlinedIcon /> },
  { key: "training", label: "Federated Training", icon: <HubOutlinedIcon /> },
  { key: "security", label: "Security", icon: <ShieldOutlinedIcon /> },
];

const Settings = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { i18n } = useTranslation();
  const { user } = useAuth();
  const { applyTrainingDefaults } = useAppContext();

  const [tab, setTab] = useState("general");
  const [language, setLanguage] = useState(() => loadUiPrefs().language || "en");
  const [timezone, setTimezone] = useState(() => loadUiPrefs().timezone || "America/Toronto");
  const [training, setTraining] = useState(() => loadTrainingDefaults());
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ open: false, msg: "", severity: "success" });
  // Password reset (secure email-link flow; the app never handles the password).
  const [pwSending, setPwSending] = useState(false);
  const handlePasswordReset = async () => {
    setPwSending(true);
    try {
      const res = await apiPost("/user/password-reset", {});
      setToast({ open: true, msg: res?.message || "A password reset link has been sent to your email.", severity: "success" });
    } catch (e) {
      const msg = String(e?.message || "");
      setToast({ open: true, msg: msg.includes("429") ? "A reset link was just sent. Check your email." : "Couldn't send the reset link. Try again shortly.", severity: msg.includes("429") ? "warning" : "error" });
    } finally {
      setPwSending(false);
    }
  };

  // On mount, reconcile the cached values with the server (source of truth).
  // Skip overwriting fields the user is mid-edit (dirty) so we don't clobber
  // unsaved changes if the fetch resolves late.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { training: srvTraining, ui } = await fetchTrainingDefaults();
      if (cancelled || dirty) return;
      setTraining(srvTraining);
      if (ui?.language) setLanguage(ui.language);
      if (ui?.timezone) setTimezone(ui.timezone);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showToast = (msg, severity = "success") => setToast({ open: true, msg, severity });
  const closeToast = () => setToast((t) => ({ ...t, open: false }));

  const setTrain = (key, value) => {
    setTraining((prev) => ({ ...prev, [key]: value }));
    setDirty(true);
  };
  const setLang = (value) => {
    setLanguage(value);
    setDirty(true);
  };
  const setTz = (value) => {
    setTimezone(value);
    setDirty(true);
  };

  const handleSave = async () => {
    // Persist to the server (per-user users.preferences) with a localStorage
    // write-through cache. Apply UI prefs locally regardless of sync result.
    setSaving(true);
    try {
      const [trainingRes, uiRes] = await Promise.all([
        saveTrainingDefaults(training),
        saveUiPrefs({ language, timezone }),
      ]);

      // Push the new defaults into shared context so the Configuration screen
      // picks them up immediately, without waiting for a remount or relogin.
      applyTrainingDefaults?.(training);
      i18n.changeLanguage(language);
      setDirty(false);

      const synced = trainingRes?.synced && uiRes?.synced;
      if (synced) {
        showToast("Settings saved · new run configs will use these defaults", "success");
      } else {
        showToast("Saved on this device · will sync to your account when back online", "warning");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    setTraining(loadTrainingDefaults());
    const ui = loadUiPrefs();
    setLanguage(ui.language || "en");
    setTimezone(ui.timezone || "America/Toronto");
    setDirty(false);
    showToast("Changes discarded", "info");
  };

  const handleResetDefaults = () => {
    setTraining({ ...DEFAULT_TRAINING_DEFAULTS });
    setDirty(true);
    showToast("Reset to platform defaults. Save to apply", "info");
  };

  // ── shared style fragments ─────────────────────────────────────────────────
  const cardSx = {
    backgroundColor: "var(--fx-paper)",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 2,
    p: 2.5,
  };
  const sectionTitleSx = { color: 'var(--fx-text-primary)', fontWeight: 700, fontSize: "0.875rem", letterSpacing: "-0.005em" };
  const sectionDescSx = { color: colors.grey[500], fontSize: "0.75rem", mt: 0.25, mb: 2 };
  const labelSx = { fontSize: "0.8125rem", fontWeight: 600, color: 'var(--fx-text-primary)' };
  const helpSx = { fontSize: "0.6875rem", color: colors.grey[500], mt: 0.5 };

  const fieldProps = {
    size: "small",
    fullWidth: true,
    variant: "outlined",
    sx: { "& .MuiInputBase-input": { fontSize: "0.8125rem" } },
  };

  const Section = ({ icon, title, desc, children, action }) => (
    <Box sx={cardSx}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: desc ? 0 : 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{
              width: 26, height: 26, borderRadius: 1,
              backgroundColor: "rgba(27,54,93,0.08)", color: 'var(--fx-text-primary)',
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
              "& svg": { fontSize: 15 },
            }}
          >
            {icon}
          </Box>
          <Typography sx={sectionTitleSx}>{title}</Typography>
        </Box>
        {action}
      </Box>
      {desc && <Typography sx={sectionDescSx}>{desc}</Typography>}
      {children}
    </Box>
  );

  const Grid2 = ({ children }) => (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>{children}</Box>
  );

  const Field = ({ label, help, children }) => (
    <Box>
      <Typography sx={{ ...labelSx, mb: 0.75 }}>{label}</Typography>
      {children}
      {help && <Typography sx={helpSx}>{help}</Typography>}
    </Box>
  );

  // small adornment factory for unit suffixes
  const unit = (text) => ({
    endAdornment: (
      <InputAdornment position="end">
        <Typography sx={{ fontSize: "0.6875rem", color: colors.grey[400] }}>{text}</Typography>
      </InputAdornment>
    ),
  });

  return (
    <Box sx={{ p: 3, maxWidth: 1440, mx: "auto" }}>
      {/* Header + save actions */}
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 1.5 }}>
        <Header
          icon={<SettingsOutlinedIcon />}
          title="Settings"
          subtitle="Manage your interface preferences and default federated training parameters."
        />
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {dirty && (
            <Typography sx={{ fontSize: "0.75rem", color: colors.amber[600], fontWeight: 600 }}>
              Unsaved changes
            </Typography>
          )}
          <Button variant="outlined" size="small" disabled={!dirty || saving} onClick={handleDiscard}>Discard</Button>
          <Button variant="contained" size="small" disabled={!dirty || saving} onClick={handleSave}>
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </Box>
      </Box>

      {/* Account context banner (read-only, sourced from Keycloak) */}
      <Box
        sx={{
          display: "flex", alignItems: "center", gap: 1.5,
          backgroundColor: "var(--fx-paper)", border: `1px solid ${theme.palette.divider}`,
          borderLeft: `3px solid ${colors.secondary[500]}`, borderRadius: 2,
          p: 1.5, px: 2, mb: 2.5,
        }}
      >
        <ShieldOutlinedIcon sx={{ fontSize: 18, color: colors.secondary[500], flexShrink: 0 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, color: 'var(--fx-text-primary)' }}>
            Signed in as{" "}
            <Box component="span" sx={{ fontFamily: MONO_FONT, color: colors.secondary[500] }}>
              {user?.email || user?.name || "user@federatex.io"}
            </Box>
          </Typography>
          <Typography sx={{ fontSize: "0.6875rem", color: colors.grey[500], mt: 0.2 }}>
            Identity, roles, and site scopes are managed in Keycloak (realm{" "}
            <Box component="span" sx={{ fontFamily: MONO_FONT }}>federatex</Box>) under Users &amp; Roles.
          </Typography>
        </Box>
      </Box>

      {/* Tab bar */}
      <Box
        role="tablist"
        sx={{
          display: "inline-flex", gap: 0.25, backgroundColor: "var(--fx-canvas)",
          border: `1px solid ${theme.palette.divider}`, borderRadius: 2,
          p: 0.4, mb: 2.5, flexWrap: "wrap",
        }}
      >
        {TABS.map((t) => {
          const selected = tab === t.key;
          return (
            <Box
              key={t.key}
              role="tab"
              aria-selected={selected}
              tabIndex={0}
              onClick={() => setTab(t.key)}
              onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setTab(t.key)}
              sx={{
                display: "inline-flex", alignItems: "center", gap: 0.6,
                px: 1.75, py: 0.7, borderRadius: 1.25, fontSize: "0.75rem", fontWeight: 600,
                cursor: "pointer", userSelect: "none", transition: "all 0.14s ease",
                color: selected ? "var(--fx-primary)" : "var(--fx-text-secondary)",
                backgroundColor: selected ? "var(--fx-raised)" : "transparent",
                border: selected ? "1px solid var(--fx-border-strong)" : "1px solid transparent",
                boxShadow: selected ? "var(--fx-shadow-sm)" : "none",
                "&:hover": selected ? {} : { color: 'var(--fx-text-primary)' },
                "& svg": { fontSize: 15 },
              }}
            >
              {t.icon}
              {t.label}
            </Box>
          );
        })}
      </Box>

      {/* ── GENERAL ─────────────────────────────────────────────────────────── */}
      {tab === "general" && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Section icon={<TuneOutlinedIcon />} title="Language &amp; region" desc="Console language and the timezone used to display run timestamps and activity. Applied when you save.">
            <Grid2>
              <Field label="Interface language">
                <TextField {...fieldProps} select value={language} onChange={(e) => setLang(e.target.value)}>
                  <MenuItem value="en">English</MenuItem>
                  <MenuItem value="fr">Français</MenuItem>
                </TextField>
              </Field>
              <Field label="Timezone" help="Used when displaying dates and times across the platform.">
                <TextField {...fieldProps} select value={timezone} onChange={(e) => setTz(e.target.value)}>
                  <MenuItem value="America/Toronto">America/Toronto (EST)</MenuItem>
                  <MenuItem value="America/Vancouver">America/Vancouver (PST)</MenuItem>
                  <MenuItem value="Europe/Paris">Europe/Paris (CET)</MenuItem>
                  <MenuItem value="UTC">UTC</MenuItem>
                </TextField>
              </Field>
            </Grid2>
          </Section>
        </Box>
      )}

      {/* ── FEDERATED TRAINING DEFAULTS ─────────────────────────────────────── */}
      {tab === "training" && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Section
            icon={<HubOutlinedIcon />}
            title="Default hyperparameters"
            desc="These values pre-fill the hyperparameter panel each time you start a new run configuration. They can still be changed per run."
            action={
              <Button
                size="small"
                variant="text"
                startIcon={<RestartAltOutlinedIcon sx={{ fontSize: 15 }} />}
                onClick={handleResetDefaults}
                sx={{ fontSize: "0.6875rem" }}
              >
                Reset to platform defaults
              </Button>
            }
          >
            <Grid2>
              <Field label="Model architecture" help="Default tabular model for new runs.">
                <TextField {...fieldProps} select value={training.model_type} onChange={(e) => setTrain("model_type", e.target.value)}>
                  {MODEL_OPTIONS.map((m) => (
                    <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                  ))}
                </TextField>
              </Field>
              <Field label="Global rounds" help="Recommended 1–10. Federated aggregation rounds run by the server.">
                <TextField
                  {...fieldProps}
                  type="number"
                  inputProps={{ min: 1, max: 100 }}
                  value={training["num-server-rounds"]}
                  onChange={(e) => setTrain("num-server-rounds", Number(e.target.value))}
                  InputProps={unit("rounds")}
                />
              </Field>
              <Field label="Local epochs" help="Training passes each client runs on its local data per round.">
                <TextField
                  {...fieldProps}
                  type="number"
                  inputProps={{ min: 1, max: 100 }}
                  value={training["local-epochs"]}
                  onChange={(e) => setTrain("local-epochs", Number(e.target.value))}
                  InputProps={unit("epochs")}
                />
              </Field>
              <Field label="Batch size" help="Powers of 2 (32, 64, 128).">
                <TextField
                  {...fieldProps}
                  type="number"
                  inputProps={{ min: 1 }}
                  value={training["batch-size"]}
                  onChange={(e) => setTrain("batch-size", Number(e.target.value))}
                  InputProps={unit("items")}
                />
              </Field>
              <Field label="Learning rate" help="Recommended 0.001–0.01.">
                <TextField
                  {...fieldProps}
                  type="number"
                  inputProps={{ min: 0, step: 0.001 }}
                  value={training["learning-rate"]}
                  onChange={(e) => setTrain("learning-rate", Number(e.target.value))}
                  InputProps={unit("rate")}
                />
              </Field>
            </Grid2>
          </Section>
        </Box>
      )}

      {/* ── SECURITY ────────────────────────────────────────────────────────── */}
      {tab === "security" && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, maxWidth: 720 }}>
          <SecurityMfaCard />
          <Section icon={<ShieldOutlinedIcon />} title="Password" desc="We'll email you a secure, single-use link to set a new password. Passwords are never entered or stored in this app.">
            <Button
              variant="outlined"
              onClick={handlePasswordReset}
              disabled={pwSending}
              sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
            >
              {pwSending ? "Sending link…" : "Send password reset link"}
            </Button>
          </Section>
        </Box>
      )}

      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={closeToast}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={closeToast} severity={toast.severity} variant="filled" sx={{ fontSize: "0.8125rem", alignItems: "center" }}>
          {toast.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Settings;
